import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { ServiceItem, ServiceTransaction } from '../../types';
import { fitLogoBox, fitPosColumn, imgFmt } from './letterhead';
import { formatDate } from './pdfUtils';
import { realActorName } from '../actorName';
import { isHourlyUnit, isMonthlyUnit } from '../serviceUnits';
import { flatKeyLabel, isFlatBookingReference, isFlatRateService, resolveFlatCoverage } from '../flatRateLogic';
import { proofMonthKey } from '../flatProofSummary';
import { getServiceLog } from '../serviceJournal';
import { isBackfilledAltEntry, isFlatProofEntry, isLegacyStamped, isMainInspectionBooking, isPostingEntry, reportNumberOf } from '../serviceLogUtils';
import { childIdFromReportRef } from '../linkedBillingMode';
import { contractedQuantity, isNepService, withNepReason } from '../nepPosition';

/**
 * Schrift und Innenabstand der Nachweistabelle. Herausgezogen, weil die
 * Pos.-Spalte ihre Breite jetzt aus den echten Schriftmetriken bestimmt — eine
 * zweite, still abweichende Kopie der Werte machte die Messung falsch. Beide
 * Werte sind unveraendert die bisherigen.
 */
const PERF_TABLE_FONT_SIZE = 9;
const PERF_TABLE_PADDING = 3;

/**
 * Erster und letzter Leistungstag, den die Tabelle des Nachweises WIRKLICH
 * auflistet.
 *
 * Gelesen wird genau derselbe Eintragsbestand, den der Tabellenaufbau weiter
 * unten durchlaeuft: sichtbare Leistungen, darin sichtbare Protokollzeilen.
 * Damit kann der Kopf der Tabelle unter ihm nicht widersprechen — er ist aus
 * ihr abgeleitet und nicht aus einer zweiten Quelle, die von ihr abweichen
 * kann.
 *
 * Bewusst NICHT der Leistungszeitraum des BELEGS: der kommt aus der Zuordnung
 * (invoiceLogic: performancePeriod) und nennt die abgerechnete Leistung. Er
 * kann frueher enden als der letzte hier aufgefuehrte Eintrag, sobald ein Teil
 * der Menge bereits von einem Vorbeleg abgerechnet wurde. Andruck, E-Rechnung
 * und DATEV nennen die Forderung, dieser Kopf die Aufstellung.
 *
 * Ohne datierte Zeile bleibt beides leer — dann gibt es keinen Zeitraum
 * auszuweisen. Bei genau einem Datum sind Anfang und Ende gleich; die
 * Formatierung verdichtet das zu einem einzelnen Datum (periodText).
 */
export const renderedPerformanceSpan = (
    services: PerformanceProofData['services'] | undefined,
): { start?: string; end?: string } => {
    let start: string | undefined;
    let end: string | undefined;
    for (const service of services ?? []) {
        if (!service?.visible) continue;
        for (const log of service.logs ?? []) {
            if (!log?.visible) continue;
            // Auf den Kalendertag kuerzen: das Protokoll traegt volle
            // Zeitstempel, und die ordneten sich im Vergleich als eigener Tag
            // ein.
            const day = log.date?.slice(0, 10);
            if (!day) continue;
            if (!start || day < start) start = day;
            if (!end || day > end) end = day;
        }
    }
    return { start, end };
};

/**
 * Zeitraum als Text — dieselbe Regel wie im Rechnungsandruck.
 *
 * Eine einzelne Seite ergibt ein DATUM, keinen angefangenen Zeitraum: "Beginn –
 * 31.07.2026" behauptete einen offenen Anfang, den der Beleg nicht nennt, und
 * die Rechnung davor schreibt in derselben Lage schlicht ein Leistungsdatum.
 * Anfang gleich Ende wird ebenso zu einem Datum verdichtet.
 */
export const periodText = (
    range?: { start?: string | null; end?: string | null } | null,
): string | undefined => {
    const start = range?.start || undefined;
    const end = range?.end || undefined;
    if (start && end) {
        return start === end ? formatDate(end) : `${formatDate(start)} – ${formatDate(end)}`;
    }
    if (end) return formatDate(end);
    return start ? formatDate(start) : undefined;
};

// Exportiert fuer die Tests der Monatspauschale — das Kuerzel 'Mon.' steht
// woertlich auf dem Kundendokument und soll dort festgenagelt sein.
export const abbreviateUnit = (unit: string): string => {
    if (!unit) return '';
    const u = unit.toLowerCase();
    // Ueber isHourlyUnit statt ueber einen eigenen Vergleich: die eigene
    // Pruefung kannte 'Stunden' und 'h', aber nicht 'Std'/'Std.'.
    if (isHourlyUnit(unit)) return 'Std.';
    // Monat ueber isMonthlyUnit, aus demselben Grund: 'Monate', 'mtl.' und
    // 'monatlich' aus Angebotseditor/LV-Import meinen dieselbe Einheit.
    if (isMonthlyUnit(unit)) return 'Mon.';
    if (u.includes('stück') || u === 'stk') return 'Stk.';
    if (u.includes('pauschal')) return 'psch';
    return unit;
};

/**
 * Deduplicate service log entries.
 * A duplicate is defined as: same serviceId + same date + same quantity.
 * Keeps the first occurrence.
 */
const deduplicateLogs = (logs: ServiceTransaction[]): ServiceTransaction[] => {
    const seen = new Set<string>();
    return logs.filter(l => {
        // Include performer to avoid merging work from different people on the same day.
        // Die NEP-Begruendung gehoert ebenfalls in den Schluessel: zwei
        // Nachbeauftragungen derselben Position am selben Tag (Menge 1, ohne
        // Beschreibung, „Auftrag A"/„Auftrag B") sind zwei Leistungen — ohne
        // sie fiele die zweite hier weg, und die Nachweismenge stuende auf 1.
        const key = `${l.serviceId}|${l.date?.split('T')[0] || ''}|${l.quantity}|${l.bookingReference || ''}|${l.description || ''}|${l.performerId || l.performerName || ''}|${l.nepReason || ''}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
};

export interface PerformanceProofData {
    services: Array<{
        id: string;
        lvPosition: string;
        clientLvPosition?: string;
        title: string;
        unit: string;
        /** Beauftragt — bei NEP das Gebuchte (contractedQuantity). */
        quantityContracted: number;
        quantityUsed: number;
        quantityBilled: number;
        assignment?: string;
        /** Eventualposition: die Vertragsspalte zeigt „NEP" statt einer Menge. */
        isNep?: boolean;
        /**
         * Durch eine MONATSPAUSCHALE gedeckt (services/flatRateLogic).
         *
         * Beim Aufbau abgeleitet, nie persistiert. Kernregel der Pauschale:
         * die gedeckte Position bleibt mit ALLEN Protokollzeilen sichtbar —
         * sie ist der Nachweis, den die Pauschale kauft — aber ihr Geldstand
         * steht still. Im Druck heisst das: Zusatz "in der Monatspauschale
         * enthalten" unter dem Titel, Spalte "Offen" ist 0, und die Zeilen
         * drucken Datum, Bericht-Nr. UND Menge, damit der Empfaenger sieht,
         * was die Pauschale abgegolten hat.
         */
        flatCovered?: boolean;
        visible: boolean;
        logs: Array<{
            id: string;
            date: string;
            description: string;
            quantity: number;
            performerName?: string;
            visible: boolean;
            /**
             * Auf DIESE Leistung wurde verzichtet (ServiceItem.waivedEntryIds).
             *
             * Sie bleibt im Nachweis stehen — erbracht ist erbracht —, traegt
             * dort aber den Vermerk "storniert" und zaehlt nicht mehr in die
             * offene Menge. Ohne den Vermerk zeigte der Nachweis eine Leistung,
             * die auf keiner Rechnung je auftaucht, und niemand koennte sagen
             * warum.
             */
            waived?: boolean;
            /**
             * Monatsbuchung der Pauschale: ihr Schluessel ('FLAT-2026-07').
             * Aus der Referenz, nicht aus dem Datum — das trug bei Altbestand
             * den Buchungstag. Unter dieser Zeile stehen im Druck die
             * Nachweiszeilen desselben Monats.
             */
            flatMonthKey?: string;
            /**
             * Nachweiszeile auf der Pauschale (NW-…, Menge 0): gedruckt als
             * eingerueckte Unterzeile ihres Monats mit "enthalten" statt einer
             * Menge. Der Monat kommt beim Druck aus dem Datum (Berliner Tag),
             * damit ein im Editor korrigiertes Datum mitwandert.
             */
            flatProof?: boolean;
        }>;
    }>;
    dateRange?: { start?: string | null; end?: string | null };
    /**
     * ABRECHNUNGSZEITRAUM des Belegs, an dem der Nachweis haengt.
     *
     * Nur die Rechnungsanlage setzt ihn (invoiceLogic: printedServicePeriod);
     * der eigenstaendige Nachweis aus dem Editor kennt keinen Beleg. Die
     * Rechnung weist ihren Abrechnungszeitraum aus, und der Nachweis daneben
     * darf keinen anderen nennen — der Empfaenger haelt zwei Angaben auf einem
     * Dokumentenstapel fuer zwei Vorgaenge.
     *
     * Wirkt AUSSCHLIESSLICH auf die Kopfzeile. Welche Protokolleintraege der
     * Nachweis zeigt, entscheidet weiterhin allein dateRange — das ist zugleich
     * sein Leistungszeitraum: der Nachweis ist der Beleg fuer die abgerechneten
     * Mengen und muss genau die Eintraege zeigen, die dahinterstehen, kumulativ
     * also auch die aus frueheren Monaten.
     */
    displayRange?: { start?: string | null; end?: string | null };
    pdfColumns?: {
        contract: boolean;
        performed: boolean;
        billed: boolean;
        open: boolean;
    };
    pdfProjectName?: string; // Editable project name for PDF only
    /**
     * Kostenstellenzeile des Belegs, an dem der Nachweis haengt — Bezeichnung
     * ("Kostenstelle", "PSP-Element", …) und Wert getrennt, weil die Infobox
     * beides in eigenen Spalten setzt. Nur der Rechnungs-Anhang fuellt das
     * Feld; der eigenstaendige Nachweis aus dem Editor kennt keinen Beleg.
     */
    pdfCostCenter?: { label: string; value: string };
}

// Report number patterns, e.g. "Bericht #46", "Bericht Nr. 46", "Zusatzleistung: Bericht #46".
// Daughter pattern is case-insensitive and allows variants (Tochter-Projekt, Tochterprojekt, T-Projekt).
const REPORT_RE = /(?:Bericht|Begehung|Report)\s*(?:#|Nr\.)\s*(\d+)/i;
const DAUGHTER_RE = /(?:Tochter-?Projekt|T-?Projekt)\s+["']?([^"':]+)["']?:\s*(?:Bericht|Begehung|Report)\s*(?:#|Nr\.)\s*(\d+)/i;

/**
 * Eine Position, die einen ganzen Tag pauschal abdeckt — der Halbtagssatz
 * ausdruecklich nicht. Dieselbe Erkennung steuert die Sortierung (Pauschale
 * zuerst) UND die Tagesregel weiter unten; auseinanderlaufende Kopien waeren
 * genau der Fehler, den die Regel verhindern soll.
 */
const isDailyFlatTitle = (title?: string): boolean => {
    const t = (title || '').toLowerCase();
    return t.includes('tagespauschale') && !t.includes('halb');
};

const sortServices = (services: ServiceItem[]): ServiceItem[] =>
    [...services].sort((a, b) => {
        // Die MONATSPAUSCHALE steht im Nachweis ganz oben — aus demselben
        // Grund, aus dem die Tagespauschale vor den uebrigen
        // Begehungspositionen steht: sie ist der Rahmen, unter dem die
        // gedeckten Positionen nur noch Nachweis sind. Massgeblich ist das
        // assignment 'flatrate' (Anzeige und Sortierung), NICHT die
        // aufgeloeste Deckung: eine halb angelegte Pauschale ohne
        // flatRate-Objekt soll trotzdem oben stehen und dort als
        // unvollstaendig auffallen statt irgendwo in der Liste unterzugehen.
        const aFlat = a.assignment === 'flatrate';
        const bFlat = b.assignment === 'flatrate';
        if (aFlat !== bFlat) return aFlat ? -1 : 1;
        // If both are inspection services, prioritize Tagespauschale (excluding halbtag)
        // so that unbooked dummy log entries naturally assign to the main Day flat rate first.
        if (a.assignment === 'inspection' && b.assignment === 'inspection') {
            const aTitle = (a.title || '').toLowerCase();
            const bTitle = (b.title || '').toLowerCase();
            const aIsTages = isDailyFlatTitle(a.title);
            const bIsTages = isDailyFlatTitle(b.title);
            const aIsHalb = aTitle.includes('halbtag') || aTitle.includes('halb-tag');
            const bIsHalb = bTitle.includes('halbtag') || bTitle.includes('halb-tag');

            if (aIsTages && !bIsTages) return -1;
            if (!aIsTages && bIsTages) return 1;
            if (!aIsHalb && bIsHalb) return -1;
            if (aIsHalb && !bIsHalb) return 1;
        }
        return (a.lvPosition || '').localeCompare(b.lvPosition || '', undefined, { numeric: true });
    });

// Prepare history entries for matching — support multi-project (child projects).
// Key format: "project name|number" (lowercase for robustness); plain number as
// fallback only for the own project.
const buildHistoryMap = (reportHistory: any[], ownProjectName: string): Map<string, any> => {
    const historyMap = new Map<string, any>();
    reportHistory.forEach(h => {
        if (!h.inspectionNumber) return;
        const cleanName = (h.projectName || '').trim().toLowerCase();
        historyMap.set(`${cleanName}|${h.inspectionNumber}`, h);
        if (cleanName === ownProjectName) {
            historyMap.set(String(h.inspectionNumber), h);
        }
        // Der belastbare Schluessel: Projekt-id + Nummer. Ihn liefert
        // fetchReportHistoryWithChildren mit; Buchungen mit `RPT-CHILD-…`
        // finden ihr Archiv darueber, unabhaengig davon, wie das Projekt
        // heute heisst.
        if (h.projectId) {
            historyMap.set(`id:${h.projectId}|${h.inspectionNumber}`, h);
        }
    });
    return historyMap;
};

/**
 * Das Archiv zu einer Buchung mit KIND-REF (`RPT-CHILD-<Tochter>-<Nr>`).
 *
 * `null`, wenn die Zeile keinen Kind-Ref traegt — dann entscheidet der
 * Text wie bisher. `undefined`, wenn sie einen traegt, das Archiv aber nicht
 * dazu geladen ist: dann darf die Zeile NICHT auf das eigene Archiv der
 * Mutter zurueckfallen. Berichtsnummern sind nur innerhalb eines Projekts
 * eindeutig; „Bericht 8 der Tochter" auf „Bericht 8 der Mutter" abzubilden
 * gab dem Nachweis von 6290-1 am 02.09.2026 zehn statt acht Begehungen im
 * Juli/August und nahm ihm sechs im Mai/Juni.
 */
const childHistoryFor = (
    l: ServiceTransaction,
    historyMap: Map<string, any>,
): any | null | undefined => {
    const kindId = childIdFromReportRef(l.reportRef);
    if (!kindId) return null;
    const nr = reportNumberOf(l);
    return nr === null ? undefined : historyMap.get(`id:${kindId}|${nr}`);
};

// Pre-scan all log entries to identify which history entries are already covered/booked.
// This avoids generating duplicate dummy logs under standard inspection services
// when another inspection service (like Tagespauschale) already handles it.
const markCoveredHistory = (
    serviceLog: ServiceTransaction[],
    historyMap: Map<string, any>,
    ownProjectName: string,
    usedHistoryIds: Set<string>
): void => {
    serviceLog.forEach(l => {
        const desc = l.description || '';
        const ausRef = childHistoryFor(l, historyMap);
        if (ausRef !== null) {
            if (ausRef) usedHistoryIds.add(ausRef.id);
            return;
        }
        const daughterMatch = desc.match(DAUGHTER_RE);
        if (daughterMatch) {
            const hist = historyMap.get(`${daughterMatch[1].trim().toLowerCase()}|${parseInt(daughterMatch[2])}`);
            if (hist) usedHistoryIds.add(hist.id);
        } else {
            const match = desc.match(REPORT_RE);
            if (match) {
                const nr = parseInt(match[1]);
                const hist = historyMap.get(`${ownProjectName}|${nr}`) || historyMap.get(String(nr));
                if (hist) usedHistoryIds.add(hist.id);
            }
        }
    });
};

// Merge one log with the archive (self & children): resolved entries take the
// archive date. A daughter entry without archive match falls through to the
// own-project match (historic behavior, kept intentionally).
//
// Ausführender: Es gewinnt `sent_by` aus dem Archiv (Migration Nr. 67) — die
// EINE Stelle, die festhält, wer den Bericht versendet hat, geschrieben
// ausschließlich vom Versandpfad. Danach erst der in der BUCHUNG festgehaltene
// Name; das ist derselbe Mensch, nur aus der zweiten Quelle (und die einzige
// für Zeilen, die der Backfill nicht erreicht hat). Der Archivname
// überschrieb ihn bisher und drückte damit den Hauptverantwortlichen des
// Projekts in den Leistungsnachweis für den Kunden, obwohl der Vertreter vor
// Ort war. Archivname bleibt nur Fallback für Altbestand ohne Buchungsnamen.
//
// „Gesetzt" heißt dabei realPersonName (services/personName.ts) und nicht bloß
// truthy: Buchungen tragen aus Altbestand die Platzhalter 'System'/'coordinator'
// und Trümmerstücke wie "Max undefined" oder " ". Die verdrängten den richtigen
// Archivnamen und standen wörtlich im Kundendokument. Dieselbe Bewertung nimmt
// cloudService.ts beim Archivieren vor.
// Leerer String statt undefined, weil ServiceTransaction.performerName ein
// Pflichtfeld ist — und weil '' die Fallback-Ketten weiter unten so passieren
// lässt, wie sie es vorher taten.
const performer = (...candidates: Array<string | undefined | null>): string => {
    for (const candidate of candidates) {
        const name = realActorName(candidate);
        if (name) return name;
    }
    return '';
};

const mergeLogWithHistory = (
    l: ServiceTransaction,
    historyMap: Map<string, any>,
    ownProjectName: string,
    usedHistoryIds: Set<string>
): ServiceTransaction => {
    const desc = l.description || '';

    // Zuerst der Ref — er ist die einzige Angabe, die nicht raten muss.
    const ausRef = childHistoryFor(l, historyMap);
    if (ausRef !== null) {
        if (!ausRef) return l;
        usedHistoryIds.add(ausRef.id);
        const nr = reportNumberOf(l);
        const projectName = String(ausRef.projectName || '').trim();
        // Den Text nur dort in die Kind-Form bringen, wo er einen Bericht
        // benennt. `Zusatzleistung: …` bleibt stehen — daran haengt, dass die
        // Zeile keine Begehung ist.
        const alsBericht = isMainInspectionBooking(l) || DAUGHTER_RE.test(desc);
        return {
            ...l,
            date: ausRef.date,
            description: alsBericht
                ? `Tochter-Projekt "${projectName}": Bericht Nr. ${nr}${ausRef.time ? ` (${ausRef.time} Uhr)` : ''}`
                : desc,
            performerName: performer(ausRef.sentByName, l.performerName, ausRef.conductedByName, ausRef.reportData?.metadata?.conductedByName),
        };
    }

    const daughterMatch = desc.match(DAUGHTER_RE);
    if (daughterMatch) {
        const projectName = daughterMatch[1].trim();
        const nr = parseInt(daughterMatch[2]);
        const hist = historyMap.get(`${projectName.toLowerCase()}|${nr}`);
        if (hist) {
            usedHistoryIds.add(hist.id);
            return {
                ...l,
                date: hist.date, // Use archive date if available
                description: `Tochter-Projekt "${projectName}": Bericht Nr. ${nr}${hist.time ? ` (${hist.time} Uhr)` : ''}`,
                performerName: performer(hist.sentByName, l.performerName, hist.conductedByName, hist.reportData?.metadata?.conductedByName)
            };
        }
    }

    const match = desc.match(REPORT_RE);
    if (match) {
        const nr = parseInt(match[1]);
        const hist = historyMap.get(`${ownProjectName}|${nr}`) || historyMap.get(String(nr));
        if (hist) {
            usedHistoryIds.add(hist.id);
            return {
                ...l,
                date: hist.date,
                description: `Bericht Nr. ${nr}${hist.time ? ` (${hist.time} Uhr)` : ''}`,
                performerName: performer(hist.sentByName, l.performerName, hist.conductedByName, hist.reportData?.metadata?.conductedByName)
            };
        }
    }
    return l;
};

// Add archive entries that are not represented by any log yet (dummy logs, quantity 1).
//
// STUNDENPOSITION: steht am Tag des Berichts schon eine echte Buchung (oder
// eine fruehere Archivzeile), kommt keine Archivzeile dazu — die Menge 1 waere
// dort eine erfundene Stunde. Bisher raeumte die Tagesregel solche Paare ab;
// Stundenpositionen nehmen an ihr nicht mehr teil (applyDailyFlatRule). Bei
// 6266 lag Bericht Nr. 1 unter dem alten Projektnamen im Archiv, fand seine
// Buchung nicht und haette den Nachweis auf 87 statt 86 Std. gehoben. Der
// Bericht gilt trotzdem als vergeben, damit ihn keine andere Position aufnimmt.
const appendMissingHistoryLogs = (
    logs: ServiceTransaction[],
    serviceId: string,
    reportHistory: any[],
    usedHistoryIds: Set<string>,
    hourly = false
): void => {
    const day = (d?: string | null) => (d || '').split('T')[0];
    reportHistory.forEach(hist => {
        if (usedHistoryIds.has(hist.id)) return;
        if (hourly && logs.some(l => (l.quantity || 0) > 0 && !isDrawdownEntry(l) && day(l.date) === day(hist.date))) {
            usedHistoryIds.add(hist.id);
            return;
        }
        logs.push({
            id: hist.id,
            date: hist.date,
            serviceId,
            quantity: 1,
            // Hier gibt es keine Buchung, aus der ein Ausführender kommen
            // könnte — es bleibt das Archiv: erst der Versender (`sent_by`),
            // dann der Archivname. Auch der wird geprüft: Alt-Archive tragen
            // 'System' als Durchführenden.
            performerName: performer(hist.sentByName, hist.conductedByName),
            description: `Bericht Nr. ${hist.inspectionNumber}${hist.time ? ` (${hist.time} Uhr)` : ''}`,
            isSubstitution: false
        } as any);
        usedHistoryIds.add(hist.id);
    });
};

// Legacy entries can have date = null. Don't crash on .split and don't
// silently drop them from the date filter — keep them so the proof
// still shows the work (user can fix the date in the editor).
const filterByDateRange = (
    logs: ServiceTransaction[],
    dateRange?: { start?: string | null; end?: string | null }
): ServiceTransaction[] => {
    let out = logs;
    if (dateRange?.start) {
        const startStr = dateRange.start; // YYYY-MM-DD
        out = out.filter(l => !l.date || l.date.split('T')[0] >= startStr);
    }
    if (dateRange?.end) {
        const endStr = dateRange.end; // YYYY-MM-DD
        out = out.filter(l => !l.date || l.date.split('T')[0] <= endStr);
    }
    return out;
};

// ─── Was ist ERBRACHT, was ist ABGERECHNET? ────────────────────────────────
//
// Eine BUCHUNG ist keine erbrachte Leistung. Welche Zeile eine Buchung ist,
// sagt die Journal-Buchungsart (`service_journal.kind`), sobald sie bekannt
// ist — aus dem Eintrag selbst oder aus dem Buchungsart-Register; sonst gilt
// der Praefix- und Textrueckfall (`BU-` Rechnung/Storno, `MB-` manuelle
// Verbuchung, `VZ-` Verzicht, `RN-` Ruecknahme, Text „manuelle Verbuchung").
// Alle vier fallen aus der erbrachten Menge und aus der gedruckten Liste.
//
// `ALT-` ist eine aus einer Alt-Rechnung uebernommene Position
// (isBackfilledAltEntry, Journal: legacy_backfill). Sie wurde vor dem Zeitraum
// dieses Nachweises erbracht und berechnet, und der Name an ihr ist der des
// Bearbeiters, der den Import gefahren hat, nicht der des Ausfuehrenden —
// gedruckt behauptete der Nachweis eine Leistung dieser Person. Sie zaehlt
// deshalb nicht als erbracht, wohl aber als abgerechnet: die Alt-Rechnung IST
// der Beleg dieser Menge.
//
// `MANUAL-` OHNE den Verbuchungstext ist ARBEIT, ebenso `LEGACY-AUTO`: beide
// bleiben in der erbrachten Menge und in der Liste und gelten ZUGLEICH als
// abgerechnet. Dafuer gibt es KEINE Journal-Buchungsart — im Journal sind sie
// `work`, die Abrechnungsaussage steckt allein in der Belegkonvention. Sie
// steht deshalb weiterhin als Praefixregel hier und nicht im kind.
// Die rund 100 ALTEN `MANUAL-`-Zeilen MIT dem Text „Manuelle Verbuchung" sind
// dagegen keine Arbeit: das Journal fuehrt sie als `manual_posting`, der
// Rueckfall erkennt sie am Text — sie sind Buchungen und fallen aus der
// erbrachten Menge (gleiche Lesart wie cockpitLogic.ts, isWorkEntry).
//
// ─── AENDERUNGEN DES RUECKFALLS gegenueber dem Bestand ────────────────────
// Der Rueckfall (Beleg und Text) beantwortet dieselbe Frage wie vorher, aber
// an drei Stellen anders. Vollstaendig:
//
//   1. SICHTBAR: die Spuren von Verzicht (`VZ-`) und Ruecknahme (`RN-`)
//      erscheinen nicht mehr in der gedruckten Liste je Leistung. Sie tragen
//      Menge 0, es bewegt sich also keine Summe — aber sie standen dort als
//      Zeile ohne Leistung.
//   2. `MB-` (manuelle Verbuchung ohne Beleg) trifft jetzt ueber das PRAEFIX.
//      Vorher wurde sie allein ueber den festen Markiertext erkannt. Keine
//      Zahl bewegt sich: in PROD traegt JEDE `MB-`-Zeile diesen Text; die
//      Praefixregel schliesst nur die Luecke fuer eine textlose MB-Zeile.
//   3. Die Textregel ist jetzt `/manuelle\s+verbuchung/i` statt eines festen
//      Teilstrings. Doppelte Leerzeichen und Zeilenumbrueche zwischen den
//      beiden Woertern zaehlen damit mit; vorher fielen sie durch.
//
// UNVERAENDERT: der Beleg wird vor der Frage normalisiert (trimmen,
// Grossschreibung, siehe `normalisiert`). Der Nachweis liest Altbestand, in
// dem Referenzen mit Leerzeichen und in Kleinschreibung vorkommen, und hat sie
// seit jeher als Buchung gelesen. Ohne diese Normalisierung druckte
// `'  bu-2026-11007  '` als erbrachte Leistung samt Namen eines Ausfuehrenden
// und erhoehte quantityUsed.

/**
 * Der Eintrag in der Form, in der beide Praedikate unten fragen.
 *
 * Steht der Beleg schon normalisiert da (der Regelfall — jede maschinell
 * geschriebene Referenz tut das), kommt der Eintrag SELBST zurueck: mit `id`
 * und `kind`, damit Journal-Art und Buchungsart-Register entscheiden koennen.
 *
 * AENDERT das Normalisieren den Beleg, ist die Zeile Altbestand — und dann
 * darf das Register NICHT gefragt werden. Die Journal-Ableitung normalisiert
 * ihrerseits nicht (serviceJournal.journalKind, byte-gleich mit SQL Nr. 60):
 * `'  bu-2026-1  '` trifft dort kein Praefix und wird als `work` gefuehrt und
 * als `work` registriert. Waere die id dabei, faende `isPostingEntry` genau
 * diese Eintragung, nennte die Buchung Arbeit und druckte sie als erbrachte
 * Leistung — der Fehler, den die Normalisierung gerade verhindern soll.
 * Zurueck kommt deshalb ein Objekt OHNE `id` und `kind`: fuer solche Zeilen
 * entscheidet weiterhin der normalisierte Beleg (und der Text), nie das
 * Register.
 *
 * Der Eingabeeintrag bleibt in beiden Faellen unveraendert.
 */
const normalisiert = (
    l: ServiceTransaction,
): Partial<Pick<ServiceTransaction, 'id' | 'kind' | 'bookingReference' | 'description'>> => {
    const roh = l.bookingReference || '';
    const norm = roh.trim().toUpperCase();
    if (norm === roh) return l;
    return { bookingReference: norm, description: l.description };
};

const isDrawdownEntry = (l: ServiceTransaction): boolean => {
    const n = normalisiert(l);
    return isPostingEntry(n) || isBackfilledAltEntry(n);
};

// Billed includes all manual/auto bookings that represent a billing action.
const isBillingEntry = (l: ServiceTransaction): boolean => {
    const n = normalisiert(l);
    return isPostingEntry(n) ||
        isBackfilledAltEntry(n) ||
        // Belegkonvention ohne Journal-Entsprechung — siehe oben.
        (n.bookingReference || '').startsWith('MANUAL-') ||
        // Beim Verbuchen gestempelt: alt mit 'LEGACY-AUTO', neu mit der
        // urspruenglichen Referenz und `bookedBy = 'System (Legacy)'`.
        isLegacyStamped(l);
};

const sumQuantities = (logs: ServiceTransaction[]): number =>
    logs.reduce((acc, l) => acc + (l.quantity || 0), 0);

/**
 * VERSCHLUSSZEILE der Umstellung auf die Monatspauschale: Monatsschluessel
 * FLAT-JJJJ-MM mit Menge 0 (flatRateSwitchLogic.planFlatSwitch). Sie sperrt
 * einen Monat, der vor dem Stichtag je Einzelposition abgerechnet wurde —
 * ein Buchhaltungsriegel, keine Leistung. Datiert ist sie auf den Tag der
 * Umstellung: bei 5633 standen 26 davon auf dem 31.08.2026 und druckten in
 * jedem Nachweis ueber August als "0,0 Mon."-Zeilen, dazu zogen sie den
 * Leistungszeitraum im Kopf bis zum Umstellungstag. Deshalb fallen sie wie
 * Verzicht und Ruecknahme aus der Liste; eine Summe bewegen sie nicht.
 */
const isFlatLockEntry = (l: ServiceTransaction): boolean =>
    isFlatBookingReference(l.bookingReference) && (l.quantity || 0) === 0;

/** Pauschalposition — auch die halb angelegte ohne Deckungsobjekt (wie sortServices). */
const isFlatRateLike = (s: ServiceItem): boolean => isFlatRateService(s) || s.assignment === 'flatrate';

// Standardize the display description of one log for the editor/PDF.
const toOutputLog = (l: ServiceTransaction, s: ServiceItem) => {
    let desc = l.description || '';

    const reportMatch = desc.match(REPORT_RE);
    const isZusatzleistung = desc.toUpperCase().includes('ZUSATZLEISTUNG');
    // Nachweiszeile auf der Pauschale (NW-…): sie IST der Bericht. Ohne diese
    // Ausnahme fiele sie unten in den Zweig fuer Nicht-Begehungspositionen und
    // druckte statt "Bericht Nr. 11" viermal den Titel der Pauschale.
    const flatProof = isFlatProofEntry(l) && isFlatRateLike(s);

    if (reportMatch && flatProof) {
        // Die Tochter bleibt stehen: Mutter und Tochter fuehren eigene
        // Nummernkreise, "Bericht Nr. 3" allein waere zweideutig.
        const daughter = desc.match(DAUGHTER_RE);
        desc = daughter
            ? `Tochter-Projekt "${daughter[1].trim()}": Bericht Nr. ${daughter[2]}`
            : `Bericht Nr. ${reportMatch[1]}`;
    } else if (reportMatch) {
        // For non-inspection services (like Baubesprechung, Fahrtkosten etc),
        // use the service title instead of the generic report number
        if (s.assignment !== 'inspection') {
            desc = s.title;
        } else {
            // For primary inspections, keep numbering
            desc = `Bericht Nr. ${reportMatch[1]}`;
        }
    } else if (isZusatzleistung && (!desc || desc.length < 30)) {
        // Generic "Zusatzleistung" without report info -> use title
        desc = s.title;
    }

    desc = desc.replace(/\(ERZWUNGEN\)/g, '').replace(/\(Bereits aktiv\)/g, '').replace(/"/g, '').trim();
    // NEP: die Begruendung kommt NACH allen Umschreibungen dazu. Vorher
    // eingemischt ging sie hier verloren — „Bericht Nr. 7" oder der
    // Positionstitel ersetzten den ganzen Text (Codex-Befund 18.09.2026).
    desc = withNepReason(desc, l.nepReason);
    // Der VERZICHT haengt an der id des Protokolleintrags, nicht am Text: der
    // Text ist im Nachweis-Editor frei aenderbar, die id nicht.
    const waived = (s.waivedEntryIds || []).map(String).includes(String(l.id));
    return {
        id: l.id,
        date: l.date,
        description: desc,
        quantity: l.quantity || 0,
        waived,
        // Letzte Schranke vor der Koordinatorenspalte des Kundendokuments:
        // Buchungen ohne Archivtreffer (manuelle Erfassung, Zusatzleistungen)
        // laufen nicht durch mergeLogWithHistory und brächten ihren Platzhalter
        // sonst ungefiltert bis ins PDF.
        performerName: realActorName(l.performerName),
        visible: true,
        // Nur wo gesetzt — Positionen ohne Pauschale behalten ihre Zeilenform.
        ...(isFlatBookingReference(l.bookingReference) ? { flatMonthKey: String(l.bookingReference).trim() } : {}),
        ...(flatProof ? { flatProof: true } : {}),
    };
};

// --- DAILY FLAT RULE: on any given day only ONE inspection entry may count.
// The parent project's own entry wins over child entries ("Tochter-Projekt …").
// Losing entries are zeroed and hidden so neither the editor preview nor the
// PDF counts them. Mutates in place.
//
// Wogegen die Regel sich richtet, ist die DOPPELTE ERFASSUNG DERSELBEN
// Leistung an einem Tag: die Tochter bucht auf das LV der Mutter, beide legen
// denselben Vor-Ort-Termin ab — oder ein Altbestand traegt ihn zweimal.
//
// Sie darf dagegen NICHT zwei VERSCHIEDENE Leistungen gegeneinander
// ausspielen. Fuer Projekt 5606 tat sie genau das: an drei Tagen fanden eine
// turnusmaessige Sicherheitsbegehung (2.7.10, 200 EUR) UND eine DGUV-Begehung
// (2.7.50, 350 EUR) statt. Beide sind erbracht und beide sind abzurechnen; der
// Nachweis zeigte von fuenf DGUV-Begehungen nur zwei, weil die uebrigen drei
// gegen die Sicherheitsbegehung desselben Tages verloren.
//
// Deshalb konkurrieren Eintraege nur noch,
//   • wenn sie zur SELBEN Position gehoeren — dann ist es eine Dublette, oder
//   • wenn eine Tagespauschale beteiligt ist — die deckt den ganzen Tag
//     pauschal ab, daneben ist fuer eine zweite Begehung kein Platz.
// Echte Tagespauschalen-Faelle bleiben damit vollstaendig abgedeckt: weil
// Toechter auf das LV der Mutter buchen, landet ihre Buchung ohnehin auf
// derselben Position wie die der Mutter.
//
// STUNDENPOSITIONEN nehmen gar nicht teil. Zwei Eintraege am selben Tag sind
// dort Mehrzeit, keine Dublette — die Rechnung zaehlt beide, also muss der
// Nachweis beide einzeln ausweisen. Bei 6266 nullte die Regel eine Nachbuchung
// von 6 Std. am Tag von Bericht #19; abgerechnet waren 86 Std., belegt 80.
const applyDailyFlatRule = (builtServices: PerformanceProofData['services']): void => {
    const isChildEntry = (desc?: string) => /Tochter-?Projekt|T-?Projekt/i.test(desc || '');
    const byDay = new Map<string, Array<{ sIdx: number; lIdx: number; child: boolean }>>();
    builtServices.forEach((s, sIdx) => {
        if (s.assignment !== 'inspection') return;
        if (isHourlyUnit(s.unit)) return;
        s.logs.forEach((l, lIdx) => {
            if (!(l.quantity > 0)) return;
            const day = (l.date || '').split('T')[0];
            if (!day) return;
            const arr = byDay.get(day) || [];
            arr.push({ sIdx, lIdx, child: isChildEntry(l.description) });
            byDay.set(day, arr);
        });
    });

    const drop = (e: { sIdx: number; lIdx: number }) => {
        const svc = builtServices[e.sIdx];
        const log = svc.logs[e.lIdx];
        svc.quantityUsed = Math.max(0, svc.quantityUsed - (log.quantity || 0));
        log.quantity = 0;
        log.visible = false;
    };

    byDay.forEach(entries => {
        if (entries.length < 2) return;

        // Ist eine Tagespauschale im Spiel, gilt der Tag als Ganzes und alle
        // Eintraege des Tages konkurrieren miteinander. Sonst nur die Eintraege
        // je Position untereinander.
        const dayIsFlat = entries.some(e => isDailyFlatTitle(builtServices[e.sIdx].title));
        const groups = dayIsFlat
            ? [entries]
            : [...entries.reduce((byService, e) => {
                  const arr = byService.get(e.sIdx) || [];
                  arr.push(e);
                  byService.set(e.sIdx, arr);
                  return byService;
              }, new Map<number, typeof entries>()).values()];

        groups.forEach(group => {
            if (group.length < 2) return;
            const winner = group.find(e => !e.child) || group[0];
            group.forEach(e => { if (e !== winner) drop(e); });
        });
    });
};

/**
 * Build the editable data structure from project data.
 */
export const buildPerformanceProofData = (
    project: any,
    dateRange?: { start?: string | null; end?: string | null },
    reportHistory: any[] = []
): PerformanceProofData => {
    const meta = project.data.metadata;
    const services: ServiceItem[] = meta.globalData?.services || [];
    const rawLog: ServiceTransaction[] = getServiceLog(project.data);
    const ownProjectName = (meta.projectName || '').trim().toLowerCase();

    const serviceLog = deduplicateLogs(rawLog);
    const sortedServices = sortServices(services);
    // Die Deckung der Monatspauschale hier SELBST aufloesen statt auf den
    // Stempel flatCoveredBy zu vertrauen: meta.globalData.services ist der
    // rohe Blob-Bestand und laeuft nicht durch getNormalizedServices — der
    // Stempel kann fehlen. resolveFlatCoverage rechnet dieselbe Regel und
    // ist deterministisch, beide Wege ergeben dieselbe Zuordnung.
    const flatCoverage = resolveFlatCoverage(services);
    const historyMap = buildHistoryMap(reportHistory, ownProjectName);

    const usedHistoryIds = new Set<string>();
    markCoveredHistory(serviceLog, historyMap, ownProjectName, usedHistoryIds);

    const builtServices = sortedServices.map(s => {
        const itemLogs = serviceLog.filter(l => l.serviceId === s.id);

        // --- HYBRID LOGIC: Merge with Archive (Self & Children) ---
        const processedLogs = itemLogs.map(l => mergeLogWithHistory(l, historyMap, ownProjectName, usedHistoryIds));

        // If this is an inspection service, add missing history entries (that are NOT in logs yet)
        if (s.assignment === 'inspection') {
            appendMissingHistoryLogs(processedLogs, s.id, reportHistory, usedHistoryIds, isHourlyUnit(s.unit));
        }

        // Filter on resolved dates, then chronological order
        const finalLogs = filterByDateRange(processedLogs, dateRange)
            .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

        // Recalculate quantities based on the logs (Filtered or Unfiltered).
        // In a Performance Proof, we want to reflect exactly what the logs show,
        // especially since we might be merging logs from child projects.
        const used = sumQuantities(finalLogs.filter(l => !isDrawdownEntry(l)));
        const billed = sumQuantities(finalLogs.filter(isBillingEntry));

        // Autoritativer Verbuchungs-Stand: die relationale Spalte project_services.quantity_billed
        // (hier s.quantityBilled) ist die Quelle der Wahrheit. Der aus dem serviceLog
        // rekonstruierte "billed"-Wert dient nur als Fallback für Altdaten ohne diese Spalte.
        // Verhindert, dass bereits verbuchte Positionen nach Storno/Legacy/Quellendrift
        // wieder als offen (quantityUsed - quantityBilled) erscheinen.
        const authoritativeBilled = (typeof s.quantityBilled === 'number') ? s.quantityBilled : billed;

        return {
            id: s.id,
            lvPosition: s.lvPosition || '',
            clientLvPosition: s.clientLvPosition,
            title: s.title,
            unit: s.unit,
            // NEP: beauftragt ist das Gebuchte — hier das aus dem Protokoll
            // abgeleitete `used`, nicht der Positionszaehler, damit der
            // Nachweis in sich stimmt (contractedQuantity fuer die Regel).
            quantityContracted: isNepService(s) ? used : contractedQuantity(s),
            quantityUsed: used,
            quantityBilled: authoritativeBilled,
            assignment: s.assignment,
            ...(isNepService(s) ? { isNep: true } : {}),
            flatCovered: flatCoverage.has(s.id),
            visible: true,
            logs: finalLogs
                .filter(l => !isDrawdownEntry(l) && !isFlatLockEntry(l))
                .map(l => toOutputLog(l, s))
        };
    });

    applyDailyFlatRule(builtServices);

    return {
        services: builtServices,
        dateRange
    };
};

/**
 * Format a quantity for display in PDF.
 * Uses German locale: comma as decimal separator.
 */
const formatQty = (qty: number, unitShort: string): string => {
    if (qty % 1 === 0) return `${qty},0 ${unitShort}`;
    return `${qty.toFixed(1).replace('.', ',')} ${unitShort}`;
};

/** Eine Druckzeile im Aufbau (buildProofRows). */
type ProofRowEntry = {
    date: string;
    desc: string;
    secondaryInfo: string;
    performer: string;
    quantity: number;
    waived?: boolean;
    flatMonthKey?: string;
    flatProof?: boolean;
};

type ProofRowItem =
    | { header: string; entry?: undefined; sub?: undefined }
    | { header?: undefined; entry: ProofRowEntry; sub: boolean };

/**
 * Einrueckung der Nachweiszeilen unter ihrem Monat. Normale Leerzeichen plus
 * Mittelpunkt: jsPDF erhaelt fuehrende Leerzeichen, setzt aber nur
 * WinAnsi-Zeichen — ein Geviert-Leerzeichen kaeme als Fremdzeichen an.
 */
const PROOF_SUB_INDENT = '   · ';

/**
 * Reihenfolge der Druckzeilen einer Pauschale MIT Nachweiszeilen.
 *
 * Die uebrigen Zeilen bleiben in ihrer Reihenfolge (der Editor erlaubt
 * Umsortieren per Ziehen); jede Nachweiszeile wandert unter die
 * Monatsbuchung ihres Monats (Berliner Tag, proofMonthKey).
 *
 * Nachweiszeilen, deren Monat im Blatt KEINE Monatsbuchung hat (laufender
 * Monat, noch nicht gebucht), stehen unter einer eigenen Zwischenzeile
 * "MM/JJJJ – Monat noch nicht gebucht" — eingeordnet vor der ersten
 * spaeteren Monatsbuchung, sonst am Ende. Bewusst MIT Zwischenzeile: ohne sie
 * stuende die eingerueckte Zeile optisch unter dem vorigen Monat, und der
 * Empfaenger rechnete eine Septemberbegehung dem bezahlten August zu. Die
 * Zwischenzeile traegt keine Menge; die Summe der Pauschale bleibt die der
 * gebuchten Monate.
 */
const orderFlatProofEntries = (entries: ProofRowEntry[]): ProofRowItem[] => {
    const proofsByMonth = new Map<string, ProofRowEntry[]>();
    entries.forEach(e => {
        if (!e.flatProof) return;
        const key = proofMonthKey(e.date) ?? '';
        const list = proofsByMonth.get(key);
        if (list) list.push(e);
        else proofsByMonth.set(key, [e]);
    });
    const bookedKeys = new Set(entries.filter(e => !e.flatProof && e.flatMonthKey).map(e => e.flatMonthKey as string));
    // Ohne lesbares Datum ('') ganz ans Ende — es gehoert zu keinem Monat.
    const pending = [...proofsByMonth.keys()]
        .filter(k => !bookedKeys.has(k))
        .sort((a, b) => (a || '￿').localeCompare(b || '￿'));

    const out: ProofRowItem[] = [];
    const flushBefore = (limit?: string) => {
        while (pending.length > 0 && (limit === undefined || (pending[0] !== '' && pending[0] < limit))) {
            const key = pending.shift() as string;
            out.push({ header: key ? `${flatKeyLabel(key)} – Monat noch nicht gebucht` : 'ohne Datum – keinem Monat zugeordnet' });
            (proofsByMonth.get(key) ?? []).forEach(p => out.push({ entry: p, sub: true }));
        }
    };
    const placed = new Set<string>();
    entries.forEach(e => {
        if (e.flatProof) return;
        if (e.flatMonthKey) flushBefore(e.flatMonthKey);
        out.push({ entry: e, sub: false });
        // Ein doppelt gebuchter Monat ist ein Anlagefehler, den die
        // Rechnungspruefung meldet — seine Nachweise stehen nur einmal.
        if (e.flatMonthKey && !placed.has(e.flatMonthKey)) {
            placed.add(e.flatMonthKey);
            (proofsByMonth.get(e.flatMonthKey) ?? []).forEach(p => out.push({ entry: p, sub: true }));
        }
    });
    flushBefore();
    return out;
};

/**
 * Tabellenzeilen des Nachweises — pur herausgezogen, damit die Zeilenlogik
 * ohne jsPDF pruefbar ist (kein jsdom im Projekt). generatePerformanceProof
 * ruft genau diese Funktion; eine zweite, still abweichende Kopie der Regeln
 * im Generator waere derselbe Fehler, den PERF_TABLE_FONT_SIZE schon einmal
 * beseitigt hat.
 *
 * MONATSPAUSCHALE:
 *  - Die Pauschalzeile selbst druckt jede Monatsbuchung mit Datum,
 *    Beschreibung ('Monatspauschale MM/YYYY') und Menge — auch fuer einen
 *    Monat ganz ohne Begehung: die Pauschale kauft Bereitschaft, nicht
 *    Stueckzahl, und ihre Zeile darf nicht davon abhaengen, dass darunter
 *    Begehungszeilen stehen.
 *  - Eine GEDECKTE Position traegt den Zusatz "in der Monatspauschale
 *    enthalten" und druckt ihre Zeilen mit Datum, Bericht-Nr. UND Menge —
 *    der Empfaenger soll sehen, was die Pauschale abgegolten hat. Ihre
 *    Spalte "Offen" ist 0 (Kernregel: der Geldstand einer gedeckten
 *    Position steht still), gedruckt wie jede andere 0 als '-'.
 *  - NACHWEISZEILEN auf der Pauschale (NW-…, Menge 0) stehen eingerueckt
 *    unter der Monatsbuchung ihres Monats, mit "enthalten" statt einer Menge
 *    (orderFlatProofEntries). Erbracht/Offen der Pauschale bleiben die
 *    Summe der Monate.
 */
export const buildProofRows = (
    visibleServices: PerformanceProofData['services'],
    cols: { contract: boolean; performed: boolean; billed: boolean; open: boolean },
): any[][] => {
    const rows: any[][] = [];

    visibleServices.forEach((s, sIdx) => {
        const unitShort = abbreviateUnit(s.unit);
        const posLabel = s.clientLvPosition || String(sIdx + 1);
        const visibleLogs = s.logs.filter(l => l.visible);
        // Ueber isHourlyUnit statt ueber einen eigenen Vergleich: eine
        // 'Std'-Position fiel hier in den Stueck-Zweig und druckte eine LEERE
        // Menge — der Leser sah ein nacktes Datum, wo 3,5 Std. gebucht sind.
        const isHourly = isHourlyUnit(s.unit);
        // Volle Zeile (Datum + Beschreibung + Menge) auch fuer die
        // Pauschalposition (Einheit Monat) und fuer gedeckte Positionen: bei
        // der Monatszeile IST die Beschreibung der Monat, und ein
        // angebrochener Monat (0,5) muss seine Menge zeigen; bei gedeckten
        // Positionen ist die Zeile der Nachweis der abgegoltenen Leistung.
        const detailed = isHourly || isMonthlyUnit(s.unit) || !!s.flatCovered;

        // Collect all log entries (flat, no project grouping)
        const entries: ProofRowEntry[] = [];
        visibleLogs.forEach(l => {
            let desc = l.description || '';
            let secondaryInfo = '';

            // Detect Daughter Project
            const childMatch = desc.match(/Tochter-Projekt\s+["']?([^"':]+)["']?:\s*(?:Bericht|Begehung|Report)\s*(?:#|Nr\.)\s*(\d+)/i);
            if (childMatch) {
                secondaryInfo = childMatch[1].trim();
                desc = `Bericht Nr. ${childMatch[2]}`;
            } else {
                // Check if it's just a normal report number
                const normalMatch = desc.match(/(?:Bericht|Begehung|Report)\s*(?:#|Nr\.)\s*(\d+)/i);
                if (normalMatch) desc = `Bericht Nr. ${normalMatch[1]}`;
            }

            entries.push({
                date: l.date,
                desc,
                secondaryInfo,
                performer: l.performerName || '',
                quantity: l.quantity,
                waived: l.waived,
                ...(l.flatMonthKey ? { flatMonthKey: l.flatMonthKey } : {}),
                ...(l.flatProof ? { flatProof: true } : {}),
            });
        });

        // Die verzichtete Menge unter den GEZEIGTEN Zeilen. Bewusst aus den
        // Zeilen und nicht aus quantityWaived: der Nachweis zeigt oft einen
        // Zeitraum, und ein Verzicht aus einem anderen Monat duerfte die
        // offene Menge dieses Blattes nicht druecken. Ein Altverzicht ohne
        // Zuordnung (vor Nr. 70) bleibt damit wie bisher unsichtbar — er
        // rechnet nichts falsch, er sagt nur nichts.
        const waivedShown = entries.reduce((sum, e) => sum + (e.waived ? (e.quantity || 0) : 0), 0);

        // Build cell content line by line
        const leistungLines: string[] = [s.title];
        const koordinatorLines: string[] = [''];
        const mengeLines: string[] = [detailed ? '-' : ''];

        // Der Zusatz direkt unter dem Titel, damit auf dem Kundendokument
        // steht, WARUM diese Position kein offenes Geld traegt — ohne ihn
        // laese der Empfaenger die 0 in "Offen" als Rechenfehler.
        if (s.flatCovered) {
            leistungLines.push('in der Monatspauschale enthalten');
            koordinatorLines.push('');
            mengeLines.push('');
        }

        if (entries.length > 0) {
            // Empty line for spacing between title and date list
            leistungLines.push('');
            koordinatorLines.push('');
            mengeLines.push('');

            const pushEntry = (entry: ProofRowEntry, sub = false) => {
                const d = new Date(entry.date);
                const dateStr = d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });

                // Der Vermerk steht in DERSELBEN Zeile wie die Leistung: der
                // Empfaenger soll nicht zwei Stellen zusammenlesen muessen, um
                // zu verstehen, warum diese Begehung auf keiner Rechnung steht.
                const vermerk = entry.waived ? ' — storniert, nicht berechnet' : '';

                if (sub) {
                    // Nachweiszeile unter ihrem Monat: Datum, Bericht, Name —
                    // und "enthalten" statt "0,0 Mon.". Die Menge 0 ist keine
                    // Aussage ueber die Leistung, sondern darueber, dass der
                    // Monat sie schon traegt.
                    const fullDesc = entry.secondaryInfo ? `${entry.secondaryInfo}: ${entry.desc}` : entry.desc;
                    leistungLines.push(`${PROOF_SUB_INDENT}${dateStr} ${fullDesc || ''}`.trimEnd() + vermerk);
                    koordinatorLines.push(entry.performer);
                    mengeLines.push('enthalten');
                } else if (detailed) {
                    // Stunden/Monat/gedeckt: "DD.MM.YY Beschreibung" | Performer | "X,X Std."
                    const fullDesc = entry.secondaryInfo ? `${entry.secondaryInfo}: ${entry.desc}` : entry.desc;
                    leistungLines.push(`${dateStr} ${fullDesc || ''}`.trimEnd() + vermerk);
                    koordinatorLines.push(entry.performer);
                    mengeLines.push(formatQty(entry.quantity, unitShort));
                } else {
                    // Tagespauschale: "DD.MM.YY [Daughter Project]" | Performer | ""
                    const lineText = entry.secondaryInfo ? `${dateStr} (${entry.secondaryInfo})` : dateStr;
                    leistungLines.push(lineText + vermerk);
                    koordinatorLines.push(entry.performer);
                    mengeLines.push('');
                }
            };

            if (entries.some(e => e.flatProof)) {
                orderFlatProofEntries(entries).forEach(item => {
                    if (item.header !== undefined) {
                        leistungLines.push(item.header);
                        koordinatorLines.push('');
                        mengeLines.push('');
                    } else {
                        pushEntry(item.entry, item.sub);
                    }
                });
            } else {
                entries.forEach(entry => pushEntry(entry));
            }
        }

        const leistungText = leistungLines.join('\n').trimEnd();
        const koordinatorText = koordinatorLines.join('\n').trimEnd();
        const mengeText = mengeLines.join('\n').trimEnd();

        const row: any[] = [posLabel, leistungText, koordinatorText, mengeText];

        // Optional quantity columns.
        // KERNREGEL der Monatspauschale: eine gedeckte Position hat kein
        // offenes Geld — quantityUsed laeuft weiter, der Geldstand steht
        // still. Ohne diesen Riegel stuende hier used - billed, und der
        // Nachweis forderte woertlich Geld, das die Pauschale schon deckt.
        // Eine stornierte Leistung wird nicht mehr gefordert — sonst stuende
        // sie hier als offener Posten, waehrend die Zeile darueber sagt, dass
        // sie niemand berechnet.
        const open = s.flatCovered ? 0 : Math.max(0, s.quantityUsed - s.quantityBilled - waivedShown);
        // NEP: keine Vertragsmenge — der Kunde hat sie nicht bestellt, sie
        // steht als Kennzeichen da, und die erbrachte Spalte sagt, was
        // nachbeauftragt wurde.
        if (cols.contract) row.push(s.isNep ? 'NEP' : `${s.quantityContracted} ${unitShort}`);
        if (cols.performed) row.push(`${s.quantityUsed} ${unitShort}`);
        if (cols.billed) row.push(`${s.quantityBilled} ${unitShort}`);
        if (cols.open) row.push(open > 0 ? `${open} ${unitShort}` : '-');

        rows.push(row);
    });

    return rows;
};

/**
 * Generate PDF from (possibly edited) proof data.
 * 
 * Default layout (Muster-PDF style):
 *   Pos. | Leistung & Verlauf | Koordinator
 * 
 * For Tagespauschale-type services:
 *   Each log = date line with coordinator name in Koordinator column
 * 
 * For Stunden-type services:
 *   Each log = "DD.MM.YY Beschreibung" | "Performer    X,X Std."
 * 
 * Optional quantity columns (Vertrag/Erbracht/Abgerechnet/Offen) can be toggled on.
 */
export const generatePerformanceProof = async (
    project: any,
    userProfile: any,
    proofDataOrDateRange?: PerformanceProofData | { start?: string | null; end?: string | null }
): Promise<jsPDF> => {
    const meta = project.data.metadata;

    let data: PerformanceProofData;
    if (proofDataOrDateRange && 'services' in proofDataOrDateRange && Array.isArray(proofDataOrDateRange.services)) {
        data = proofDataOrDateRange as PerformanceProofData;
    } else {
        const dateRange = proofDataOrDateRange as { start?: string | null; end?: string | null } | undefined;
        data = buildPerformanceProofData(project, dateRange);
    }

    const doc = new jsPDF({ compress: true });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    let currentY = 20;

    // Use editable project name or fall back to metadata
    const displayProjectName = data.pdfProjectName || meta.projectName || '-';
    const displayProjectNumber = meta.projectNumber || '-';

    // --- HEADER ---
    if (userProfile?.company_logo) {
        try {
            // Seitenverhaeltnis UND Breite begrenzen (fitLogoBox), Format aus
            // der DataURL — sonst wird ein breites Logo ueber den Blattrand
            // hinaus skaliert und ein PNG als JPEG eingebettet.
            const logoHeight = 15;
            const box = fitLogoBox(doc.getImageProperties(userProfile.company_logo), 60, logoHeight);
            if (box) {
                doc.addImage(userProfile.company_logo, imgFmt(userProfile.company_logo), pageWidth - margin - box.width, 15 + (logoHeight - box.height), box.width, box.height);
            }
        } catch (e) { console.warn("Logo error", e); }
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.setTextColor(30, 41, 59);
    doc.text("Leistungsnachweis", margin, currentY + 8);

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(100, 116, 139);
    // Ueber denselben formatDate wie Rechnung und Gutschrift: der Nachweis
    // haengt hinten an der Rechnung, und zwei Datumsformate auf einem
    // Dokumentenstapel liest der Empfaenger als zwei Absender. Frueher stand
    // hier ein eigenes toLocaleDateString mit eigener Parselogik.
    let dateText = `Stand: ${formatDate(new Date().toISOString())}`;
    // Zwei Zeitraeume, wie auf der Rechnung davor:
    //
    //   Abrechnungszeitraum  worueber abgerechnet wird — die Angabe des Belegs
    //                        (displayRange, nur bei der Rechnungsanlage).
    //   Leistungszeitraum    wann geleistet wurde — erster und letzter Tag der
    //                        Eintraege, die die TABELLE DARUNTER auflistet
    //                        (renderedPerformanceSpan).
    //
    // Der eigenstaendige Nachweis aus dem Editor kennt keinen Beleg und damit
    // keinen Abrechnungszeitraum; dort steht der Leistungszeitraum allein in
    // der Standzeile, und der Kopf bleibt einzeilig wie bisher.
    const billingRange = data.displayRange;
    // Aus den Zeilen selbst und NICHT aus dem Filterfenster (dateRange) oder
    // der Zuordnung: der Kopf soll die Tabelle beschreiben, die unter ihm
    // steht. Jede andere Quelle kann von ihr abweichen — das Filterfenster
    // reicht bis zum Monatsletzten, und die Zuordnung endet frueher als der
    // letzte aufgefuehrte Eintrag, sobald ein Teil der Menge aus einem
    // Vorbeleg stammt. Der Empfaenger haelt beides fuer einen Fehler.
    //
    // Der ausgewiesene Leistungszeitraum des BELEGS (Andruck, E-Rechnung,
    // DATEV) bleibt davon unberuehrt: der kommt aus der Zuordnung
    // (invoiceLogic: performancePeriod) und nennt die abgerechnete Leistung.
    // Hier geht es um die Aufstellung, dort um die Forderung.
    const rendered = renderedPerformanceSpan(data.services);
    const hasBilling = !!(billingRange?.start || billingRange?.end);
    const hasRendered = !!(rendered.start || rendered.end);
    if (hasBilling) {
        dateText += ` | Abrechnungszeitraum: ${periodText(billingRange)}`;
    } else if (hasRendered) {
        dateText += ` | Leistungszeitraum: ${periodText(rendered)}`;
    } else if (data.dateRange?.start || data.dateRange?.end) {
        // Eine leere Tabelle hat keinen Leistungszeitraum. Das Filterfenster
        // sagt dann wenigstens, wonach gesucht wurde — gebraucht vom
        // eigenstaendigen Nachweis, dessen Kopf sonst gar keinen Zeitraum
        // naennte. Ausdruecklich als Zeitraum der ABFRAGE benannt, damit er
        // nicht als Leistung gelesen wird.
        dateText += ` | Zeitraum der Auswertung: ${periodText(data.dateRange)}`;
    }
    doc.text(dateText, margin, currentY + 14);
    currentY += 25;

    // Der Leistungszeitraum in eigener Zeile, sobald oben der
    // Abrechnungszeitraum steht — sonst fehlte er ganz. Kumulativ fallen die
    // beiden weit auseinander: der Beleg rechnet ueber Juni/Juli ab und listet
    // Leistungen ab dem 05.03. auf. Ohne diese Zeile widersprechen Kopf und
    // Tabelle einander stillschweigend, und der Empfaenger haelt die
    // Aufstellung fuer falsch.
    //
    // Die Zeile wird RUECKWAERTS in den Abstand unter der Standzeile gesetzt
    // (currentY - 6) und schiebt danach den Rest um dieselben 6 mm nach unten.
    // Ohne dieses Nachschieben zeichnete die Projektbox — sie kommt gleich
    // danach und ist gefuellt — genau ueber die Zeile hinweg. Ohne die Zeile
    // bleibt der Abstand unveraendert, der einzeilige Kopf druckt also wie
    // bisher.
    //
    // Ohne datierte Zeile entfaellt sie: eine Tabelle ohne Leistungstage hat
    // keinen Leistungszeitraum, und das Filterfenster an seine Stelle zu setzen
    // hiesse, eine Leistung zu behaupten, die die Aufstellung nicht zeigt.
    if (hasBilling && hasRendered) {
        doc.text(`Leistungszeitraum: ${periodText(rendered)}`, margin, currentY - 6);
        currentY += 6;
    }

    // --- PROJECT INFO ---
    // Mit Kostenstellenzeile (Rechnungs-Anhang) waechst die Box um eine Zeile.
    const costCenter = data.pdfCostCenter;
    const infoBoxHeight = costCenter ? 35 : 25;
    doc.setDrawColor(226, 232, 240);
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(margin, currentY, pageWidth - (margin * 2), infoBoxHeight, 2, 2, 'FD');
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);
    doc.text("PROJEKT", margin + 5, currentY + 8);
    doc.text("PROJEKT-NR.", margin + 5, currentY + 18);
    if (costCenter) doc.text(costCenter.label.toUpperCase(), margin + 5, currentY + 28);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(15, 23, 42);
    doc.text(displayProjectName, margin + 40, currentY + 8);
    doc.text(displayProjectNumber, margin + 40, currentY + 18);
    if (costCenter) doc.text(costCenter.value, margin + 40, currentY + 28);
    currentY += infoBoxHeight + 10;

    // --- TABLE ---
    // Default 4 columns: Pos. | Leistung & Verlauf | Koordinator | (Menge for hourly)
    // Optional additional columns: Vertrag, Gesamt, Abger., Offen
    const cols = data.pdfColumns || { contract: false, performed: false, billed: false, open: false };
    const visibleServices = data.services.filter(s => s.visible);
    // Zeilenbau in buildProofRows (pur, oben) — hier bleibt nur, was jsPDF
    // wirklich braucht: Kopfzeile, Spaltenbreiten, autoTable.
    const rows = buildProofRows(visibleServices, cols);

    // Build headers & column styles
    const headRow: string[] = ['Pos.', 'Leistung & Verlauf', 'Koordinator', ''];
    // Die Pos.-Spalte traegt die LV-Nummer des Auftraggebers
    // (`clientLvPosition`) und muss mit ihr mitwachsen: eine umgebrochene
    // Nummer ("1.1.10" wird zu "1.1.1" mit "0." darunter) liest sich als eine
    // ANDERE Position. 18 mm bleibt die Mindestbreite, damit kurze Nummern
    // unveraendert drucken; 30 mm die Kappung.
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(PERF_TABLE_FONT_SIZE);
    const posCol = fitPosColumn(
        rows.map((r) => String(r[0] ?? '')),
        (t) => doc.getTextWidth(t),
        { min: 18, max: 30, paddingX: PERF_TABLE_PADDING, fontSize: PERF_TABLE_FONT_SIZE },
    );
    const colStyles: Record<number, any> = {
        0: {
            cellWidth: posCol.width,
            fontStyle: 'bold',
            halign: 'center',
            // Nur im Notfall kleiner setzen (Nummer passt selbst bei der
            // Kappung nicht) — sonst uebersteuerte es auch die Kopfzeile.
            ...(posCol.fontSize < PERF_TABLE_FONT_SIZE ? { fontSize: posCol.fontSize } : {}),
        },
        1: { cellWidth: 'auto' },
        2: { cellWidth: 45 },
        3: { cellWidth: 22, halign: 'right', textColor: [100, 116, 139] }
    };
    let colIdx = 4;
    if (cols.contract) { headRow.push('Vertrag'); colStyles[colIdx] = { cellWidth: 22, halign: 'center', textColor: [100, 116, 139] }; colIdx++; }
    if (cols.performed) { headRow.push('Gesamt'); colStyles[colIdx] = { cellWidth: 22, halign: 'center', fontStyle: 'bold' }; colIdx++; }
    if (cols.billed) { headRow.push('Abger.'); colStyles[colIdx] = { cellWidth: 22, halign: 'center', textColor: [22, 163, 74] }; colIdx++; }
    if (cols.open) { headRow.push('Offen'); colStyles[colIdx] = { cellWidth: 22, halign: 'center', textColor: [37, 99, 235], fontStyle: 'bold' }; colIdx++; }

    autoTable(doc, {
        startY: currentY,
        head: [headRow],
        body: rows,
        theme: 'grid',
        headStyles: {
            fillColor: [241, 245, 249],
            textColor: [71, 85, 105],
            fontSize: 8,
            fontStyle: 'bold',
            lineWidth: 0.1,
            lineColor: [226, 232, 240]
        },
        styles: {
            fontSize: PERF_TABLE_FONT_SIZE,
            textColor: [51, 65, 85],
            cellPadding: PERF_TABLE_PADDING,
            lineColor: [226, 232, 240],
            lineWidth: 0.1,
            overflow: 'linebreak'
        },
        columnStyles: colStyles,
    });

    // --- FOOTER ---
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setDrawColor(226, 232, 240);
        doc.setLineWidth(0.1);
        doc.line(margin, pageHeight - 15, pageWidth - margin, pageHeight - 15);
        doc.setFontSize(8);
        doc.setTextColor(148, 163, 184);
        if (userProfile?.company_name) doc.text(userProfile.company_name, margin, pageHeight - 10);
        doc.text(`Seite ${i} von ${pageCount}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
    }

    return doc;
};